/**
 * Typed localStorage boundary. See wp7-client-ui-input.md § "localStorage".
 *
 * Contract:
 *   - Read: always succeeds. Corrupt / absent → defaults, silent.
 *   - Write: JSON-encode the whole struct; localStorage failures (quota,
 *     Safari private mode) swallowed — prefs are convenience, not
 *     load-bearing state.
 *
 * MVP contents: `lastJoinedMatchId` (a nicety for the Home screen).
 * The pattern exists so future prefs (audio volume, keybinds) drop in
 * with a schema addition + parser default.
 */

import { z } from "zod";
import { OPPONENT_SEAT_SCHEMA } from "@pit/protocol";
import {
  STORED_KEYMAP_SCHEMA,
  parseKeymap,
  type Keymap,
} from "./input/keybinding/index.js";
import {
  AURA_CATEGORIES,
  DEFAULT_NAMEPLATE_AURA_CATEGORIES,
} from "./champions/auraIcons.js";
import {
  SPELL_QUEUE_WINDOW_MS_DEFAULT,
  clampSpellQueueWindowMs,
} from "./input/spellQueue.js";

/** Storage key. Namespaced so multiple @pit apps could coexist on one origin. */
export const PREFS_STORAGE_KEY = "pit.prefs";

/** Aura-category enum for prefs (mirrors the client aura-category axis, #137). */
const AURA_CATEGORY_PREF_SCHEMA = z.enum(AURA_CATEGORIES);

/**
 * HUD prefs (#137/#138) — cosmetic client-side toggles + the nameplate
 * major-aura selection. Every field defaults, so a legacy/absent blob degrades
 * to the shipped defaults (the whole-prefs resilience contract). These are the
 * "customizable via config/prefs" knobs the nameplate/frame HUD reads.
 */
export const HUD_PREFS_SCHEMA = z.object({
  /** #137 — show the major-aura icon row on world nameplates. */
  nameplateAuras: z.boolean().default(true),
  /** #137 — which aura categories count as "major" on nameplates/frames. */
  nameplateAuraCategories: z
    .array(AURA_CATEGORY_PREF_SCHEMA)
    .default([...DEFAULT_NAMEPLATE_AURA_CATEGORIES]),
  /** #138 — show the consolidated unit-frame stack (bottom of screen). */
  unitFrames: z.boolean().default(true),
});

export type HudPrefs = z.infer<typeof HUD_PREFS_SCHEMA>;

/** Shipped HUD defaults (all features on, default major-aura selection). */
export const DEFAULT_HUD_PREFS: HudPrefs = {
  nameplateAuras: true,
  nameplateAuraCategories: [...DEFAULT_NAMEPLATE_AURA_CATEGORIES],
  unitFrames: true,
};

/**
 * WASD strafe-axis convention (#351) — which axis convention the local
 * player's held WASD keys resolve against:
 *   - "facing" — strafe relative to the character's current FACING (today's
 *                behavior). Default.
 *   - "screen" — strafe relative to the camera's FIXED screen axes (`d`
 *                always screen-right, `w` always screen-up, etc.),
 *                regardless of facing.
 * Wire-carried on every `moveDir` message (`@pit/sim` resolves it — see
 * `directionalDelta`), NOT a client-side key remap (see the #351 issue for
 * the rationale: a client-side remap assumes facing stays roughly static
 * while a key is held, which #346's always-face-mouse breaks).
 */
export const WASD_STRAFE_MODE_SCHEMA = z.union([
  z.literal("facing"),
  z.literal("screen"),
]);

export type WasdStrafeMode = z.infer<typeof WASD_STRAFE_MODE_SCHEMA>;

/** Default: facing-relative, preserving pre-#351 behavior. */
export const DEFAULT_WASD_STRAFE_MODE: WasdStrafeMode = "facing";

/**
 * Spell-queue window (#797), MILLISECONDS — how early a cast keypress may
 * land before the current cast/GCD/cooldown ends and still be HELD and fired
 * on the first legal tick (`input/spellQueue.ts`) instead of being dropped.
 *
 * Stored in ms (matching WoW's `SpellQueueWindow`, which is what this mirrors)
 * and converted to whole ticks at the boundary — never the other way round.
 * Legal range is `[0, 210]`, and a stored value outside it is CLAMPED to the
 * bound by `loadSpellQueueWindowMs` (a typo'd 5000 becomes 210, not a
 * five-second queue). `0` means the feature is OFF — the kill switch.
 *
 * `.catch(...)` mirrors every other pref's resilience contract: a corrupt/
 * legacy value degrades to the shipped default without discarding the rest of
 * prefs.
 */
export const SPELL_QUEUE_WINDOW_MS_SCHEMA = z.number().catch(SPELL_QUEUE_WINDOW_MS_DEFAULT);

/**
 * Ability slot LAYOUT (#417) — the player's drag-and-drop-chosen VISUAL
 * arrangement of a champion's action-bar slots, keyed by champion id so
 * switching champions never clobbers another champion's saved layout. This
 * is a pure client-side DISPLAY / input-resolution concern: it is never
 * wire-transmitted and never touches the sim, which continues casting off
 * the champion's normal canonical/submitted ability order untouched (see
 * `champions/abilityLayout.ts` for the permutation-sanitizing + visual↔
 * canonical remap logic that sits on top of this raw storage).
 *
 * Shape: one array per champion, `layout[visualSlot] = canonicalAbilitySlot`.
 * A malformed/legacy entry (wrong length, non-permutation, corrupt) degrades
 * to `[]` here — `abilityLayout.ts`'s sanitizer treats an entry that isn't a
 * valid full permutation as "no saved layout" and falls back to identity
 * (today's canonical order), so a corrupt value can never desync the visual
 * bar from a real ability.
 *
 * FUTURE (noted, not implemented here): once accounts + a database exist,
 * this per-champion layout should migrate to be account-associated instead
 * of purely local — the per-champion-id keying already shapes it for that
 * migration (swap the storage backend, keep the key/value shape).
 */
const ABILITY_LAYOUT_SCHEMA = z.array(z.number().int()).catch([]);

export const ABILITY_LAYOUTS_SCHEMA = z.record(z.string(), ABILITY_LAYOUT_SCHEMA).catch({});

export type AbilityLayouts = z.infer<typeof ABILITY_LAYOUTS_SCHEMA>;

/** Default: no saved layout for any champion — every lookup falls back to
 *  content-canonical order (see `abilityLayout.ts`). */
export const DEFAULT_ABILITY_LAYOUTS: AbilityLayouts = {};

/**
 * Tester access code (#497) — a locally-configured shared secret a tester
 * pastes in to be `testerFlagged` server-side, sent on every `hello`
 * (`net/socket.ts`). Deliberately NOT a per-account credential — see
 * `apps/server/src/testerFlag.ts`'s doc comment for why a shared code is
 * the correct minimal mechanism today. `.catch("")` mirrors every other
 * pref's resilience contract: a corrupt/legacy value degrades to "no code
 * configured" rather than discarding the rest of prefs.
 */
export const TESTER_ACCESS_CODE_SCHEMA = z.string().catch("");

export type TesterAccessCode = z.infer<typeof TESTER_ACCESS_CODE_SCHEMA>;

/** Default: no code configured — `hello` omits `testerAccessCode`. */
export const DEFAULT_TESTER_ACCESS_CODE: TesterAccessCode = "";

/**
 * `@pit/data` session token (#533) — the `auth_session` token persisted
 * across reconnects so a RETURNING guest (or signed-in player) resolves
 * back to the SAME account instead of a fresh throwaway guest every visit
 * (the issue's open question 4). Sent on every `hello`
 * (`net/socket.ts`'s `sendHello`); replaced whenever `welcome.sessionToken`
 * is non-null (a freshly-minted session — first-ever connect, or the
 * previous token had expired/was invalid). `.catch("")` mirrors every
 * other pref's resilience contract — a corrupt/legacy value degrades to
 * "no token", which just means the next `hello` mints a fresh guest
 * account, same as any brand-new player.
 */
export const SESSION_TOKEN_SCHEMA = z.string().catch("");

export type SessionToken = z.infer<typeof SESSION_TOKEN_SCHEMA>;

/** Default: no token yet — `hello` omits `sessionToken`. */
export const DEFAULT_SESSION_TOKEN: SessionToken = "";

/**
 * Practice-mode config (#499, restructured #676, team-size-parameterized
 * ISSUE-773) — the last-used practice setup, saveable/replayable:
 * re-sending the SAME shape (own champion/loadout + `teammateSeats`/
 * `opponentSeats`) reproduces the exact same match setup, including any
 * PINNED bot checkpoints (`{ kind: "bot", manifestId }`) — never a
 * re-resolved champion+tier search, so it stays reproducible even after the
 * manifest gains newer entries for the same champion/tier (see
 * `botSeat.ts`'s `addBotSeatById`). `championId`/`loadout` are stored as
 * loose strings (not branded) — same convention as `abilityLayouts`'
 * champion-id keys — the setup screen re-validates against the live
 * champion catalog before use.
 *
 * ISSUE-773: ONE slot holding EITHER team size (rather than a second,
 * `dummyPracticeConfig`-style slot) — `PracticeSetupScreen` is one screen
 * with a 1v1/3v3 toggle, so one "last-used setup" is the honest model, and
 * the saved array lengths are exactly what the mode toggle re-seeds from
 * (`resolveSavedTeamSize`). The `.refine` mirrors `ws.ts`'s own rule
 * verbatim: `opponentSeats.length === teammateSeats.length + 1`, with a
 * team size of 1 or 3 — the only two the server accepts. A saved config
 * that doesn't satisfy it (including every pre-ISSUE-773 shape, all of
 * which do) degrades to `null` via the `.catch(...)` on the prefs field,
 * i.e. "no saved config", never a throw. Never a dummy entry (see
 * `OPPONENT_SEAT_SCHEMA`'s doc comment), so no cross-validation with
 * `DUMMY_PRACTICE_CONFIG_SCHEMA` below is needed.
 */
export const PRACTICE_CONFIG_SCHEMA = z
  .object({
    championId: z.string().min(1),
    loadout: z.union([z.array(z.string().min(1)), z.null()]),
    /** 0 entries (1v1) or 2 (3v3) — the creator is never listed here. */
    teammateSeats: z.array(OPPONENT_SEAT_SCHEMA).max(2),
    /** Exactly `teammateSeats.length + 1` entries — see the `.refine`. */
    opponentSeats: z.array(OPPONENT_SEAT_SCHEMA).min(1).max(3),
  })
  .refine(
    (c) =>
      (c.teammateSeats.length === 0 || c.teammateSeats.length === 2) &&
      c.opponentSeats.length === c.teammateSeats.length + 1,
    { message: "seat plan must be 1v1 (0 + 1) or 3v3 (2 + 3)" },
  )
  .nullable();

export type PracticeConfig = z.infer<typeof PRACTICE_CONFIG_SCHEMA>;

/** Default: no practice config saved yet. */
export const DEFAULT_PRACTICE_CONFIG: PracticeConfig = null;

/**
 * Dummy-practice config (#609) — `DummyPracticeScreen`'s last-used
 * champion/loadout. No seat plan to save: a dummy-practice match is always
 * `opponentSeats: [{ kind: "dummy" }]`, never anything else, so there is
 * nothing seat-shaped worth persisting (unlike `PRACTICE_CONFIG_SCHEMA`
 * above).
 */
export const DUMMY_PRACTICE_CONFIG_SCHEMA = z
  .object({
    championId: z.string().min(1),
    loadout: z.union([z.array(z.string().min(1)), z.null()]),
  })
  .nullable();

export type DummyPracticeConfig = z.infer<typeof DUMMY_PRACTICE_CONFIG_SCHEMA>;

/** Default: no dummy-practice config saved yet. */
export const DEFAULT_DUMMY_PRACTICE_CONFIG: DummyPracticeConfig = null;

/**
 * Prefs schema. `keymap` (#64) is the persisted per-browser keybinding
 * override, stored in its versioned envelope. `.catch(null)` keeps the whole
 * prefs blob resilient: a corrupt/legacy keymap degrades to null (→ defaults
 * via the resolver) without discarding the rest of prefs. `hud` (#137/#138)
 * defaults to the shipped HUD prefs when absent.
 */
export const PREFS_SCHEMA = z.object({
  lastJoinedMatchId: z.union([z.string().min(1).max(64), z.null()]).catch(null),
  keymap: z.union([STORED_KEYMAP_SCHEMA, z.null()]).catch(null),
  hud: HUD_PREFS_SCHEMA.default(DEFAULT_HUD_PREFS),
  /** #351: persisted WASD strafe-axis preference. Same resilience contract —
   *  a corrupt/legacy value degrades to the shipped default. */
  wasdStrafeMode: WASD_STRAFE_MODE_SCHEMA.catch(DEFAULT_WASD_STRAFE_MODE),
  /** #417: persisted per-champion ability slot layouts. Same resilience
   *  contract — a corrupt/legacy value degrades to the shipped default
   *  (no saved layouts). */
  abilityLayouts: ABILITY_LAYOUTS_SCHEMA.catch(DEFAULT_ABILITY_LAYOUTS),
  /** #497: persisted tester access code. Same resilience contract — a
   *  corrupt/legacy value degrades to the shipped default (no code). */
  testerAccessCode: TESTER_ACCESS_CODE_SCHEMA.catch(DEFAULT_TESTER_ACCESS_CODE),
  /** #533: persisted `@pit/data` session token. Same resilience contract —
   *  a corrupt/legacy value degrades to the shipped default (no token). */
  sessionToken: SESSION_TOKEN_SCHEMA.catch(DEFAULT_SESSION_TOKEN),
  /** #797: persisted spell-queue window, ms. Read through
   *  `loadSpellQueueWindowMs`, which clamps it into the legal `[0, 210]`
   *  range — see the schema's doc comment. */
  spellQueueWindowMs: SPELL_QUEUE_WINDOW_MS_SCHEMA,
  /** #499: persisted practice-mode config. Same resilience contract — a
   *  corrupt/legacy value degrades to the shipped default (no saved config). */
  practiceConfig: PRACTICE_CONFIG_SCHEMA.catch(DEFAULT_PRACTICE_CONFIG),
  /** #609: persisted dummy-practice config. Same resilience contract — a
   *  corrupt/legacy value degrades to the shipped default (no saved config). */
  dummyPracticeConfig: DUMMY_PRACTICE_CONFIG_SCHEMA.catch(DEFAULT_DUMMY_PRACTICE_CONFIG),
});

export type Prefs = z.infer<typeof PREFS_SCHEMA>;

/** Structural defaults. Every field must have a default; corrupt inputs
 *  degrade gracefully rather than throwing. */
export const DEFAULT_PREFS: Prefs = {
  lastJoinedMatchId: null,
  keymap: null,
  hud: DEFAULT_HUD_PREFS,
  wasdStrafeMode: DEFAULT_WASD_STRAFE_MODE,
  abilityLayouts: DEFAULT_ABILITY_LAYOUTS,
  testerAccessCode: DEFAULT_TESTER_ACCESS_CODE,
  practiceConfig: DEFAULT_PRACTICE_CONFIG,
  dummyPracticeConfig: DEFAULT_DUMMY_PRACTICE_CONFIG,
  sessionToken: DEFAULT_SESSION_TOKEN,
  spellQueueWindowMs: SPELL_QUEUE_WINDOW_MS_DEFAULT,
};

/**
 * Load the per-browser keymap override (#64), or `null` if none/corrupt/legacy.
 * Callers pass the result to `resolveKeymap({ browser, defaults })`. Never
 * throws; a bad stored keymap silently yields null → defaults.
 */
export function loadKeymap(storage: PrefsStorage = defaultStorage()): Keymap | null {
  const stored = loadPrefs(storage).keymap;
  if (stored === null) return null;
  return parseKeymap(stored);
}

/**
 * Parse a raw string (may be `null` if the key was missing). Never throws.
 * Corrupt JSON or schema mismatch → defaults.
 */
export function parsePrefs(raw: string | null): Prefs {
  if (raw === null) return DEFAULT_PREFS;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_PREFS;
  }
  const r = PREFS_SCHEMA.safeParse(parsed);
  if (r.success) return r.data;
  // Not necessarily corrupt — could be a future-version pref shape. Also
  // silent default; there is no user-facing error surface for prefs.
  return DEFAULT_PREFS;
}

/**
 * Serialize prefs to JSON. Always succeeds — the schema guarantees a
 * plain-object shape.
 */
export function serializePrefs(prefs: Prefs): string {
  return JSON.stringify(prefs);
}

/** Storage adapter — separated so tests can inject a memory store. */
export interface PrefsStorage {
  readonly getItem: (key: string) => string | null;
  readonly setItem: (key: string, value: string) => void;
}

/** Load prefs from a storage adapter (defaults to `localStorage`). */
export function loadPrefs(storage: PrefsStorage = defaultStorage()): Prefs {
  try {
    return parsePrefs(storage.getItem(PREFS_STORAGE_KEY));
  } catch {
    return DEFAULT_PREFS;
  }
}

/**
 * Load just the HUD prefs sub-blob (#137/#138). Never throws; absent/corrupt →
 * the shipped {@link DEFAULT_HUD_PREFS}. Read by the nameplate-aura overlay and
 * the consolidated unit-frame stack.
 */
export function loadHudPrefs(storage: PrefsStorage = defaultStorage()): HudPrefs {
  return loadPrefs(storage).hud;
}

/**
 * Load just the WASD strafe-axis preference (#351). Never throws; absent/
 * corrupt → the shipped {@link DEFAULT_WASD_STRAFE_MODE}.
 */
export function loadWasdStrafeMode(storage: PrefsStorage = defaultStorage()): WasdStrafeMode {
  return loadPrefs(storage).wasdStrafeMode;
}

/** Save the WASD strafe-axis preference. Swallows storage failures. */
export function saveWasdStrafeMode(
  value: WasdStrafeMode,
  storage: PrefsStorage = defaultStorage(),
): void {
  updatePrefs({ wasdStrafeMode: value }, storage);
}

/**
 * Load the RAW saved layout for `championId` (#417), or `null` if none is
 * saved yet (never/corrupt/legacy → `[]` degrades to `null` here too — an
 * empty array carries no positional information). Callers (`abilityLayout.ts`)
 * sanitize this into a valid full permutation before use; this function does
 * no permutation validation itself, matching every other `load*` helper's
 * "raw storage read" contract.
 */
export function loadAbilityLayout(
  championId: string,
  storage: PrefsStorage = defaultStorage(),
): readonly number[] | null {
  const stored = loadPrefs(storage).abilityLayouts[championId];
  return stored === undefined || stored.length === 0 ? null : stored;
}

/** Save the layout for `championId`. Swallows storage failures. */
export function saveAbilityLayout(
  championId: string,
  layout: readonly number[],
  storage: PrefsStorage = defaultStorage(),
): void {
  const current = loadPrefs(storage).abilityLayouts;
  updatePrefs({ abilityLayouts: { ...current, [championId]: [...layout] } }, storage);
}

/**
 * Load the tester access code (#497), or `""` if none/corrupt/legacy.
 * Never throws.
 */
export function loadTesterAccessCode(storage: PrefsStorage = defaultStorage()): TesterAccessCode {
  return loadPrefs(storage).testerAccessCode;
}

/** Save the tester access code. Swallows storage failures. */
export function saveTesterAccessCode(
  value: TesterAccessCode,
  storage: PrefsStorage = defaultStorage(),
): void {
  updatePrefs({ testerAccessCode: value }, storage);
}

/**
 * Load the persisted `@pit/data` session token (#533), or `""` if none/
 * corrupt/legacy. Never throws.
 */
export function loadSessionToken(storage: PrefsStorage = defaultStorage()): SessionToken {
  return loadPrefs(storage).sessionToken;
}

/** Save the session token (`welcome.sessionToken`, whenever non-null).
 *  Swallows storage failures. */
export function saveSessionToken(
  value: SessionToken,
  storage: PrefsStorage = defaultStorage(),
): void {
  updatePrefs({ sessionToken: value }, storage);
}

/**
 * Load the saved practice-mode config (#499), or `null` if none/corrupt/
 * legacy. Never throws.
 */
export function loadPracticeConfig(storage: PrefsStorage = defaultStorage()): PracticeConfig {
  return loadPrefs(storage).practiceConfig;
}

/** Save the practice-mode config (the exact shape sent to `createMatch`,
 *  so replaying it reproduces the same setup). Swallows storage failures. */
export function savePracticeConfig(
  value: PracticeConfig,
  storage: PrefsStorage = defaultStorage(),
): void {
  updatePrefs({ practiceConfig: value }, storage);
}

/**
 * Load the saved dummy-practice config (#609), or `null` if none/corrupt/
 * legacy. Never throws.
 */
export function loadDummyPracticeConfig(
  storage: PrefsStorage = defaultStorage(),
): DummyPracticeConfig {
  return loadPrefs(storage).dummyPracticeConfig;
}

/** Save the dummy-practice config. Swallows storage failures. */
export function saveDummyPracticeConfig(
  value: DummyPracticeConfig,
  storage: PrefsStorage = defaultStorage(),
): void {
  updatePrefs({ dummyPracticeConfig: value }, storage);
}

/**
 * Load the spell-queue window in ms (#797), CLAMPED to the legal `[0, 210]`
 * range. Never throws; absent/corrupt → {@link SPELL_QUEUE_WINDOW_MS_DEFAULT}
 * (100ms = 3 ticks). Read once per `InputSurface` mount.
 */
export function loadSpellQueueWindowMs(storage: PrefsStorage = defaultStorage()): number {
  return clampSpellQueueWindowMs(loadPrefs(storage).spellQueueWindowMs);
}

/** Save the spell-queue window, clamped to the legal range so an
 *  out-of-range value can never be persisted. Swallows storage failures. */
export function saveSpellQueueWindowMs(
  value: number,
  storage: PrefsStorage = defaultStorage(),
): void {
  updatePrefs({ spellQueueWindowMs: clampSpellQueueWindowMs(value) }, storage);
}

/** Save prefs. Swallows storage failures (quota exceeded, disabled). */
export function savePrefs(prefs: Prefs, storage: PrefsStorage = defaultStorage()): void {
  try {
    storage.setItem(PREFS_STORAGE_KEY, serializePrefs(prefs));
  } catch {
    // Silent — prefs are a convenience layer.
  }
}

/** Update a single field, persisting the result. Convenience helper. */
export function updatePrefs(
  patch: Partial<Prefs>,
  storage: PrefsStorage = defaultStorage(),
): Prefs {
  const current = loadPrefs(storage);
  const next: Prefs = { ...current, ...patch };
  savePrefs(next, storage);
  return next;
}

/** Default storage: `window.localStorage` if available, else in-memory noop.
 *  Node/SSR renders never touch this — the store is client-only. */
function defaultStorage(): PrefsStorage {
  if (typeof globalThis !== "undefined" && "localStorage" in globalThis) {
    const ls = (globalThis as { readonly localStorage: PrefsStorage }).localStorage;
    return ls;
  }
  return MEMORY_STORAGE;
}

const memoryStore = new Map<string, string>();
const MEMORY_STORAGE: PrefsStorage = {
  getItem: (k) => memoryStore.get(k) ?? null,
  setItem: (k, v) => {
    memoryStore.set(k, v);
  },
};
